import React, { useState, useEffect } from 'react';
import StandardModal, { StandardModalBody, StandardModalFooter, useModalLoading } from '../../../components/modales/StandardModal';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import { LoadingButton, MoneyField, Notice } from '@routes-plan/ui';
import { companyCurrency, companyLocale } from '../../../utils/format/currency';
import axios from 'axios';
import { useSelection } from '../../../components/context/SelectionContext';
import { useT } from '../../../i18n';

interface EditPaysModalWebProps {
  open: boolean;
  onClose: () => void;
  clientId: number;
  payId: string | null;
  payDate: string | null;
  payment: number | null;
  balance: number | null;
  fullName: string | null;
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

const EditPaymentByDateModal: React.FC<EditPaysModalWebProps> = ({
  open,
  onClose,
  clientId,
  payId,
  payment,
  balance,
  fullName,
  showAlert,
  showLoading,
  hideLoading,
}) => {
  const t = useT();
  // El Cargando general queda detrás del modal: se usa el del modal estándar.
  const { loading: modalBusy, showLoading: showModalLoading, hideLoading: hideModalLoading } = useModalLoading();
  const { user_id, setUser_id } = useSelection();

  // ESTADOS INICIALES
  const [valorAbono, setValorAbono] = useState(0);
  const [selectedFormPay, setSelectedFormPay] = useState('1');
  const [selectedBank, setSelectedBank] = useState('');
  const [receiptNumber, setReceiptNumber] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [location, setLocation] = useState<string | null>(null);

  const banks = [
    { id: '1', name: 'Banco A' },
    { id: '2', name: 'Banco B' },
    { id: '3', name: 'Banco C' },
  ];
  const syncCoordinates = async () => {
    try {
      const coords = await getCoordinates();
      setLocation(coords);
      return coords;
    } catch (error) {
      setLocation(null);
      throw error;
    }
  };

  useEffect(() => {
    if (!open) {
      return;
    }

    syncCoordinates().catch(() => {
      showAlert(t('paymentReports.edit.locationWarning'), 'warning');
    });
  }, [open]);
  useEffect(() => {
    (
      async () => {
        const {data} = await axios.get('user')
        setUser_id(data.data.id)
      }
    )();
  },[]);

  // CUANDO SE ABRA EL MODAL, REINICIAR LOS ESTADOS
  useEffect(() => {
    if (open) {
      setValorAbono(payment || 0);
      setSelectedFormPay('1');
      setSelectedBank('');
      setReceiptNumber('');
      setErrorMessage('');
    }
  }, [open, payment]);

  // FUNCION PARA CERRAR Y LIMPIAR EL MODAL
  const handleClose = () => {
    setValorAbono(0);
    setSelectedFormPay('1');
    setSelectedBank('');
    setReceiptNumber('');
    setErrorMessage('');
    onClose(); // CERRAR EL MODAL
  };

  // FUNCION PARA GUARDAR LOS CAMBIOS
  const getCoordinates = (): Promise<string> => {
    return new Promise((resolve, reject) => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          position => {
            const lat = position.coords.latitude;
            const lng = position.coords.longitude;
            const coordinates = `${lat}, ${lng}`;
            resolve(coordinates);
          },
          error => {
            reject(error);
          }
        );
      } else {
        showAlert(t('paymentReports.edit.geolocationUnsupported'), 'warning');
        reject(new Error('Geolocation is not supported by this browser.'));
      }
    });
  };
  const handleAccept = async () => {
    if (selectedFormPay === '0' && (!selectedBank || !receiptNumber)) {
      setErrorMessage(t('paymentReports.edit.bankAndReceiptRequired'));
      return;
    }

    let currentLocation = location;
    if (!currentLocation) {
      try {
        currentLocation = await syncCoordinates();
      } catch (error) {
        currentLocation = null;
        showAlert(t('paymentReports.edit.coordinatesWarning'), 'warning');
      }
    }

    const formData = {
      value: valorAbono,
      userId: user_id,
      cash: selectedFormPay === '1',
      bankId: selectedFormPay === '0' ? selectedBank : null,
      bankReceipt: selectedFormPay === '0' ? receiptNumber : null,
      location: currentLocation,
      state: true,
    };
    try {
      setIsSaving(true);
      showModalLoading();
      const { data } = await axios.put(`payments/${payId}`, formData);
      showAlert(
        data?.code === 'CORRECTION_PENDING' ? t('paymentCorrections.pendingApproval') : t('paymentReports.edit.updatedOk'),
        data?.code === 'CORRECTION_PENDING' ? 'info' : 'success',
      );
      handleClose(); // CERRAR Y LIMPIAR EL MODAL DESPUÉS DE GUARDAR
    } catch (error) {
      showAlert(t('paymentReports.edit.updateError'), 'error');
    } finally {
      setIsSaving(false);
      hideModalLoading();
    }
  };

  return (
    <StandardModal
      open={open}
      onClose={handleClose}
      title={t('paymentReports.edit.title')}
      subtitle={t('paymentReports.edit.subtitle', { id: String(payId), name: String(fullName), idNumber: clientId })}
      maxWidth={600}
      loading={modalBusy || isSaving}
      composed
    >
      <StandardModalBody>
        <Box sx={{ display: 'grid', gap: 2, minWidth: 0, pt: 1 }}>
          {errorMessage && <Notice severity="error">{errorMessage}</Notice>}

          <FormControl fullWidth>
            <InputLabel id="edit-payment-method">{t('paymentReports.edit.paymentMethod')}</InputLabel>
            <Select
              labelId="edit-payment-method"
              value={selectedFormPay}
              onChange={(e) => setSelectedFormPay(e.target.value)}
              label={t('paymentReports.edit.paymentMethod')}
            >
              <MenuItem value="1">{t('paymentReports.edit.cash')}</MenuItem>
              <MenuItem value="0">{t('paymentReports.edit.transfer')}</MenuItem>
            </Select>
          </FormControl>

          {selectedFormPay === '0' && (
            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' } }}>
              <FormControl fullWidth>
                <InputLabel id="edit-payment-bank">{t('paymentReports.edit.bank')}</InputLabel>
                <Select
                  labelId="edit-payment-bank"
                  value={selectedBank}
                  onChange={(e) => setSelectedBank(e.target.value)}
                  label={t('paymentReports.edit.bank')}
                >
                  {banks.map((bank) => (
                    <MenuItem key={bank.id} value={bank.id}>
                      {bank.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label={t('paymentReports.edit.receiptNumber')}
                value={receiptNumber}
                onChange={(e) => setReceiptNumber(e.target.value)}
              />
            </Box>
          )}

          <MoneyField
            fullWidth
            label={t('paymentReports.edit.paymentValue')}
            value={Number.isFinite(valorAbono) ? valorAbono : null}
            onChange={(v) => setValorAbono(v ?? 0)}
            currency={companyCurrency()}
            locale={companyLocale()}
          />
        </Box>
      </StandardModalBody>
      <StandardModalFooter>
        <Button variant="outlined" onClick={handleClose}>{t('common.cancel')}</Button>
        <LoadingButton variant="contained" onClick={handleAccept} loading={isSaving}>{t('common.save')}</LoadingButton>
      </StandardModalFooter>
    </StandardModal>
  );
};

export default EditPaymentByDateModal;
